import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { getLogoUrl, getProfilePhotoUrl } from '../config';
import { Circle, ClipboardList, Calendar, MessageCircle, User, Mail, RefreshCw, DollarSign, ScrollText } from 'lucide-react';
import { toast } from '../components/Toast';
import logger from '../utils/logger';

const ProServiceRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [request, setRequest] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  // Formulaire comptabilité
  const [comptaForm, setComptaForm] = useState({
    montant_estime: '',
    montant_final: '',
    montant_paye: '',
    reference_paiement: '',
    mode_paiement: ''
  });
  
  const messagesEndRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [requestRes, statusesRes, messagesRes] = await Promise.all([
        api.get(`/service-requests/${id}`),
        api.get('/service-statuses'),
        api.get(`/service-requests/${id}/messages`)
      ]);
      
      const requestData = requestRes.data;
      
      // Vérifier que c'est bien le propriétaire du service
      if (!requestData.is_service_owner && user?.role !== 'admin') {
        navigate('/pro/service-requests');
        return;
      }
      
      setRequest(requestData);
      setStatuses(Array.isArray(statusesRes.data) ? statusesRes.data : []);
      setMessages(Array.isArray(messagesRes.data) ? messagesRes.data : []);
      
      // Pré-remplir le formulaire de comptabilité
      setComptaForm({
        montant_estime: requestData.montant_estime || '',
        montant_final: requestData.montant_final || '',
        montant_paye: requestData.montant_paye || '',
        reference_paiement: requestData.reference_paiement || '',
        mode_paiement: requestData.mode_paiement || ''
      });
    } catch (err) {
      logger.error(err);
      navigate('/pro/service-requests');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, user]);

  useEffect(() => {
    if (!user || (user.role !== 'author' && user.role !== 'admin')) {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate, loadData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleStatusChange = async (newStatusId) => {
    try {
      setUpdating(true);
      await api.put(`/service-requests/${id}/statut`, { statut_id: newStatusId });
      await loadData();
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors du changement de statut');
    } finally {
      setUpdating(false);
    }
  };

  const handleComptaSubmit = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);
      await api.put(`/service-requests/${id}/comptabilite`, comptaForm);
      await loadData();
      toast.success('Comptabilité mise à jour !');
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    try {
      setSending(true);
      await api.post(`/service-requests/${id}/messages`, { message: newMessage });
      setNewMessage('');
      const messagesRes = await api.get(`/service-requests/${id}/messages`);
      setMessages(Array.isArray(messagesRes.data) ? messagesRes.data : []);
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (statut) => {
    const colors = {
      'en_attente': 'bg-yellow-100 text-yellow-700',
      'en_cours': 'bg-blue-100 text-blue-700',
      'paiement_attente': 'bg-orange-100 text-orange-700',
      'paiement_recu': 'bg-teal-100 text-teal-700',
      'fourniture': 'bg-purple-100 text-purple-700',
      'termine': 'bg-green-100 text-green-700',
      'annule': 'bg-gray-100 text-gray-700',
      'refuse': 'bg-red-100 text-red-700'
    };
    return colors[statut] || 'bg-gray-100 text-gray-700';
  };

  const getPriorityIcon = (priorite) => {
    const colors = { 'basse': 'text-green-500', 'normale': 'text-yellow-500', 'haute': 'text-orange-500', 'urgente': 'text-red-500' };
    return <Circle className={`w-6 h-6 fill-current ${colors[priorite] || 'text-gray-400'}`} />;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-orange-600 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Demande non trouvée</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-10 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <Link to="/pro/service-requests" className="text-orange-600 hover:underline text-sm mb-2 block">
              ← Retour aux demandes
            </Link>
            <h1 className="text-2xl font-bold text-gray-800">Demande #{request.id}</h1>
            <p className="text-gray-600">{request.titre}</p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-3">
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusBadge(request.statut_nom)}`}>
              {request.statut_nom?.replace('_', ' ')}
            </span>
            <span className="text-2xl">{getPriorityIcon(request.priorite)}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">
            {/* Détails de la demande */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-orange-600" /> Détails de la demande</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm text-gray-500">Service</label>
                  <div className="flex items-center gap-2 mt-1">
                    {request.logo && (
                      <img src={getLogoUrl(request.logo)} alt="" className="w-8 h-8 rounded object-contain bg-gray-100" />
                    )}
                    <span className="font-medium">{request.service_titre}</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Date de demande</label>
                  <p className="font-medium">{formatDate(request.date_demande)}</p>
                </div>
                {request.date_souhaitee && (
                  <div>
                    <label className="text-sm text-gray-500 flex items-center gap-1"><Calendar className="w-4 h-4" /> Date souhaitée</label>
                    <p className="font-medium text-orange-600">
                      {new Date(request.date_souhaitee).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="text-sm text-gray-500">Description</label>
                <p className="mt-1 text-gray-700 whitespace-pre-line">{request.description}</p>
              </div>

              {request.details_supplementaires && (
                <div className="mb-4">
                  <label className="text-sm text-gray-500">Détails supplémentaires</label>
                  <p className="mt-1 text-gray-700 whitespace-pre-line">{request.details_supplementaires}</p>
                </div>
              )}
            </div>

            {/* Chat */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-orange-600" /> Discussion avec le client</h2>
              
              <div className="h-80 overflow-y-auto border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                {messages.length > 0 ? (
                  messages.map(msg => (
                    <div 
                      key={msg.id} 
                      className={`mb-3 ${msg.sender_id === user.id ? 'text-right' : 'text-left'}`}
                    >
                      <div 
                        className={`inline-block px-4 py-2 rounded-2xl max-w-[80%] ${
                          msg.type === 'systeme' || msg.type === 'notification'
                            ? 'bg-gray-200 text-gray-600 text-sm italic'
                            : msg.sender_id === user.id
                              ? 'bg-orange-600 text-white'
                              : 'bg-white border border-gray-200 text-gray-800'
                        }`}
                      >
                        <p>{msg.message}</p>
                        <p className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-orange-200' : 'text-gray-400'}`}>
                          {msg.sender_nom} • {formatDate(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-400 py-8">Aucun message</p>
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleSendMessage} className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Écrire un message..."
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                <button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition"
                >
                  {sending ? '...' : 'Envoyer'}
                </button>
              </form>
            </div>
          </div>

          {/* Colonne latérale */}
          <div className="space-y-6">
            {/* Infos client */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><User className="w-5 h-5 text-orange-600" /> Client</h2>
              <div className="flex items-center gap-3 mb-4">
                {request.photo_profil ? (
                  <img 
                    src={getProfilePhotoUrl(request.photo_profil)} 
                    alt={request.user_nom}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold text-lg">
                    {request.user_nom?.[0]}
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-800">{request.user_nom}</p>
                  <p className="text-sm text-gray-500">{request.user_email}</p>
                </div>
              </div>
              <a 
                href={`mailto:${request.user_email}`}
                className="w-full block text-center py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
              >
                <Mail className="w-4 h-4 inline mr-1" /> Envoyer un email
              </a>
            </div>

            {/* Changer le statut */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><RefreshCw className="w-5 h-5 text-orange-600" /> Changer le statut</h2>
              <div className="space-y-2">
                {statuses.map(status => (
                  <button
                    key={status.id}
                    onClick={() => handleStatusChange(status.id)}
                    disabled={updating || request.statut_id === status.id}
                    className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition ${
                      request.statut_id === status.id
                        ? 'bg-orange-500 text-white ring-2 ring-orange-300'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } disabled:cursor-default`}
                  >
                    {request.statut_id === status.id ? '✓ ' : ''}{status.nom.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            {/* Comptabilité */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5 text-orange-600" /> Comptabilité</h2>
              <form onSubmit={handleComptaSubmit} className="space-y-3">
                <div>
                  <label className="text-sm text-gray-600">Montant estimé (XOF)</label>
                  <input
                    type="number"
                    value={comptaForm.montant_estime}
                    onChange={(e) => setComptaForm(prev => ({ ...prev, montant_estime: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Montant final que doit payer le client (XOF)</label>
                  <input
                    type="number"
                    value={comptaForm.montant_final}
                    onChange={(e) => setComptaForm(prev => ({ ...prev, montant_final: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Montant payé (XOF)</label>
                  <input
                    type="number"
                    value={comptaForm.montant_paye}
                    onChange={(e) => setComptaForm(prev => ({ ...prev, montant_paye: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Mode de paiement</label>
                  <select
                    value={comptaForm.mode_paiement}
                    onChange={(e) => setComptaForm(prev => ({ ...prev, mode_paiement: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
                  >
                    <option value="">Sélectionner</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="virement">Virement bancaire</option>
                    <option value="especes">Espèces</option>
                    <option value="carte">Carte bancaire</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Référence paiement</label>
                  <input
                    type="text"
                    value={comptaForm.reference_paiement}
                    onChange={(e) => setComptaForm(prev => ({ ...prev, reference_paiement: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                <button
                  type="submit"
                  disabled={updating}
                  className="w-full py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition font-medium"
                >
                  {updating ? 'Mise à jour...' : 'Mettre à jour'}
                </button>
              </form>
            </div>

            {/* Historique */}
            {request.historique && request.historique.length > 0 && (
              <div className="bg-white rounded-xl shadow p-6">
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><ScrollText className="w-5 h-5 text-orange-600" /> Historique</h2>
                <div className="space-y-3">
                  {request.historique.map((h, i) => (
                    <div key={i} className="border-l-2 border-orange-200 pl-3 py-1">
                      <p className="text-sm text-gray-800">
                        <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadge(h.ancien_statut_nom)}`}>
                          {h.ancien_statut_nom?.replace('_', ' ') || 'Nouveau'}
                        </span>
                        <span className="mx-2">→</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadge(h.nouveau_statut_nom)}`}>
                          {h.nouveau_statut_nom?.replace('_', ' ')}
                        </span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Par {h.user_nom} • {formatDate(h.created_at)}
                      </p>
                      {h.commentaire && (
                        <p className="text-xs text-gray-600 mt-1 italic">"{h.commentaire}"</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProServiceRequestDetail;
